import { CommonParams } from '@/api/resources/common/shared/models/common.models'
import { CustomerGroupResponse } from '@/modules/configurationModule/detail/accounting/billable/customer-groups/services/customer-groups.dto'
import { customerGroupsService } from '@/modules/configurationModule/detail/accounting/billable/customer-groups/services/customer-groups.service'
import { CustomerTypeResponse } from '@/modules/configurationModule/detail/accounting/billable/customer-types/services/customer-types.dto'
import { customerTypesService } from '@/modules/configurationModule/detail/accounting/billable/customer-types/services/customer-types.service'
import { ConfigurationLocalAccountingCustomersModalBasicFormState, initialLocalAccountingCustomersModalBasicFormState } from '@/modules/configurationModule/detail/local-accounting/billable/clients/components/clients-modal-basic-form/configuration-local-accounting-billable-clients-modal-basic-form.model'
import { ConfigurationCustomerResponse, ConfigurationCustomerPatchInBatchRequest, ConfigurationCustomerCreateRequest } from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-services/configuration-local-accounting-billable-clients-customer-services.dto'
import { configurationLocalAccountingBillableCustomerService } from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-services/configuration-local-accounting-billable-clients-customer-services.service'
import { IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { StrictNullable } from '@/shared/types/app.types'
import { Vue, Component, Prop, Emit } from 'vue-property-decorator'

@Component({
    components: {
    }
})
export default class ConfigurationLocalAccountingBillableClientsModalBasicForm extends Vue {
    @Prop(String) readonly actionTitle!: string
    @Prop() readonly customer!: StrictNullable<ConfigurationCustomerResponse>

    @Emit()
    create(createdCustomer: ConfigurationCustomerResponse): ConfigurationCustomerResponse {
        return createdCustomer
    }

    @Emit()
    update(): void {}

    state: ConfigurationLocalAccountingCustomersModalBasicFormState
    constructor() {
        super()
        this.state = initialLocalAccountingCustomersModalBasicFormState()
    }

    mounted(): void {
        if (this.customer) {
            this.state = {
                ...this.state,
                customer: {
                    ...this.state.customer,
                    id: this.customer.id,
                    code: this.customer.code,
                    name: this.customer.name,
                    active: this.customer.active,
                    priorityForAutomaticAssignment: this.customer.priorityForAutomaticAssignment,
                    taxIdentificationNumber: this.customer.taxIdentificationNumber,
                    customerGroup: this.customer.group,
                    customerType: this.customer.type
                }
            }
        }
    }

    get modelValidated(): boolean {
        const customerExists = !!this.state.customer.name.length
        const taxIdentificationNumber = !!this.state.customer.taxIdentificationNumber
        const priorityForAutomaticAssignmentExists = !!this.state.customer.priorityForAutomaticAssignment
        const codeExists = !!this.state.customer.code.length
        const typeClientExists = !!this.state.customer.customerType?.id
        const groupClientExists = !!this.state.customer.customerGroup?.id
        const hasErrors: boolean = this.$validator.errors.any() || !customerExists || !codeExists ||
            !taxIdentificationNumber || !priorityForAutomaticAssignmentExists || !typeClientExists || !groupClientExists
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        return !hasErrors && isDirty
    }

    get isUpdatedForm(): boolean {
        return !!this.customer?.id
    }

    async doAction(): Promise<void> {
        if (this.isUpdatedForm) {
            const customerUpdateOperation = await this._updateOperation()
            if (customerUpdateOperation) {
                // emits an event
                this.update()
                await this.$validator.reset()
            }
        } else {
            const customerCreateOperation = await this._createOperation()
            if (customerCreateOperation) {
                // emits an event
                this.create(customerCreateOperation)
                await this.$validator.reset()
            }
        }
    }

    async getClientGroups(): Promise<CustomerGroupResponse[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'active': {
                filterType: 'boolean',
                type: 'equals',
                filter: true
            }
        }
        const response: StrictNullable<IResponseAgGrid<CustomerGroupResponse>> = await this.$_toCallSecure(customerGroupsService.paginator(filterModel, params))
        return response ? response.content : []
    }

    async getClientTypes(): Promise<CustomerTypeResponse[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'active': {
                filterType: 'boolean',
                type: 'equals',
                filter: true
            }
        }
        const response: StrictNullable<IResponseAgGrid<CustomerTypeResponse>> = await this.$_toCallSecure(customerTypesService.paginator(filterModel, params))
        return response ? response.content : []
    }

    /// SERVICES CALLS ///
    private async _addClient(
        request: ConfigurationCustomerCreateRequest
    ): Promise<StrictNullable<ConfigurationCustomerResponse>> {
        if (request) {
            const response = await this.$_toCallSecure(configurationLocalAccountingBillableCustomerService.create(
                this.state.laboratoryId!,
                request
            ))
            if (response) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                return response
            }
        }
        return null
    }

    private async _updateClient(
        request: ConfigurationCustomerPatchInBatchRequest
    ): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        if (request) {
            const response = await this.$_toCallSecure(configurationLocalAccountingBillableCustomerService.patch(
                this.state.laboratoryId!,
                [request]
            ))
            if (response) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                return response
            }
        }
        return null
    }

    /// FORM OPERATIONS ///
    private _createOperation(): Promise<StrictNullable<ConfigurationCustomerResponse>> {
        return this._addClient(
            {
                name: this.state.customer.name,
                taxIdentificationNumber: this.state.customer.taxIdentificationNumber,
                priorityForAutomaticAssignment: this.state.customer.priorityForAutomaticAssignment,
                code: this.state.customer.code,
                active: this.state.customer.active,
                customerGroupId: this.state.customer.customerGroup?.id!,
                customerTypeId: this.state.customer.customerType?.id!,
                // TODO Pending to implement
                breakdownDeliveryNotesByCaseType: false,
                breakdownDeliveryNotesByPatient: false,
                breakdownDeliveryNotesByRegime: false,
                predefinedCustomNotificationForAuthorizationId: null
            }
        )
    }

    private _updateOperation(): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        return this._updateClient(
            {
                id: this.state.customer.id!,
                name: this.state.customer.name,
                taxIdentificationNumber: this.state.customer.taxIdentificationNumber,
                priorityForAutomaticAssignment: this.state.customer.priorityForAutomaticAssignment,
                code: this.state.customer.code,
                active: this.state.customer.active,
                customerGroupId: this.state.customer.customerGroup?.id!,
                customerTypeId: this.state.customer.customerType?.id!,
                // TODO Pending to implement
                breakdownDeliveryNotesByCaseType: false,
                breakdownDeliveryNotesByPatient: false,
                breakdownDeliveryNotesByRegime: false,
                predefinedCustomNotificationForAuthorizationId: null
            }
        )
    }
}
